// Public A Go Go board shown on the org microsite tournament page, with a
// prominent link to the projectable "Pantalla Gigante". Auto-refreshes 15s.
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import { ArrowLeft, Monitor, Trophy, DollarSign, Flag } from "lucide-react";

const POLL_MS = 15000;
const toPar = (v) => (v == null ? "—" : v === 0 ? "E" : v > 0 ? `+${v}` : String(v));

export default function PublicAGoGoBoard({ slug, tournamentId, club, tournament }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    fetch(`${API}/public/clubs/${slug}/tournaments/${tournamentId}/agogo`).then((r) => r.json()).then(setData).catch(() => {});
  }, [slug, tournamentId]);
  useEffect(() => { load(); const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);

  const lb = data?.leaderboard;
  const rows = lb?.rows || [];
  const cal = data?.calcutta || {};
  const money = (v) => { try { return new Intl.NumberFormat("es-MX", { style: "currency", currency: cal.currency || "MXN", maximumFractionDigits: 0 }).format(v || 0); } catch { return `${v}`; } };
  const starts = data?.starts || {};
  const roundNums = Object.keys(starts).map(Number).sort();
  const active = roundNums.map((rn) => starts[String(rn)]).find((s) => (s?.holes || []).length);
  const activeRn = roundNums.find((rn) => (starts[String(rn)]?.holes || []).length) || 1;

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Link to={`/${slug}`} className="inline-flex items-center gap-1 text-sm text-[#1B4D3E] mb-4"><ArrowLeft className="w-4 h-4" /> {club?.name}</Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-bold tracking-widest text-[#1B4D3E]">A GO GO POR EQUIPOS</div>
            <h1 className="text-3xl font-black text-[#0F172A]">{tournament?.name}</h1>
            <p className="text-sm text-[#64748B] mt-1">Par {data?.config?.par_total} · Corte {data?.config?.cut_teams}</p>
          </div>
          <Link to={`/${slug}/tournaments/${tournamentId}/pantalla`} target="_blank"
            className="inline-flex items-center gap-2 bg-[#0B1D33] text-white px-5 py-3 rounded-xl font-bold shadow-lg hover:opacity-90"
            data-testid="agogo-bigscreen-link">
            <Monitor className="w-5 h-5" /> Pantalla Gigante
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
          {/* Leaderboard */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-bold text-[#0F172A] mb-3 flex items-center gap-2"><Trophy className="w-5 h-5 text-[#1B4D3E]" /> Clasificación</h2>
            {rows.length === 0 ? <p className="text-sm text-[#94A3B8] py-6 text-center">Sin tarjetas aún</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[#94A3B8]"><tr><th className="text-left px-2 py-1">#</th><th className="text-left px-2 py-1">Equipo</th><th className="text-right px-2 py-1">R1</th><th className="text-right px-2 py-1">R2</th><th className="text-right px-2 py-1">Tot</th><th className="text-right px-2 py-1">Par</th></tr></thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.team_id}>
                        {i === lb.cut_teams && rows.length > lb.cut_teams ? (
                          <td colSpan={6} className="text-center text-[11px] font-bold text-red-600 bg-red-50 py-1">— CORTE ({lb.cut_teams}) —</td>
                        ) : null}
                        <td className={`px-2 py-1.5 font-bold ${!r.made_cut ? "opacity-50" : ""}`}>{r.position}</td>
                        <td className={`px-2 py-1.5 ${!r.made_cut ? "opacity-50" : ""}`}>{r.name}</td>
                        <td className="px-2 py-1.5 text-right">{r.r1_complete ? r.r1_total : "·"}</td>
                        <td className="px-2 py-1.5 text-right">{r.r2_complete ? r.r2_total : (r.r2_total || "—")}</td>
                        <td className="px-2 py-1.5 text-right font-bold">{r.combined_total}</td>
                        <td className="px-2 py-1.5 text-right font-semibold text-[#1B4D3E]">{toPar(r.to_par)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Calcutta + Starts */}
          <div className="space-y-5">
            {cal.enabled ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <h2 className="font-bold text-[#0F172A] mb-3 flex items-center gap-2"><DollarSign className="w-5 h-5 text-[#1B4D3E]" /> Calcuta</h2>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-[#0B1D33] text-white rounded-xl p-3 text-center"><div className="text-[10px] text-[#E8C76A] font-bold">BOLSA</div><div className="font-black">{money(cal.total_raised)}</div></div>
                  <div className="bg-[#0B1D33] text-white rounded-xl p-3 text-center"><div className="text-[10px] text-[#E8C76A] font-bold">NETA</div><div className="font-black">{money(cal.net_pool)}</div></div>
                </div>
                {(cal.payouts || []).map((p) => (
                  <div key={p.position} className="flex items-center justify-between text-sm py-1 border-t">
                    <span className="font-bold text-[#1B4D3E]">{p.position}º</span>
                    <span className="flex-1 px-2 truncate">{p.team_name || "—"}</span>
                    <span className="font-bold text-emerald-700">{money(p.amount)}</span>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h2 className="font-bold text-[#0F172A] mb-3 flex items-center gap-2"><Flag className="w-5 h-5 text-[#1B4D3E]" /> Salidas R{activeRn}{active?.time ? ` · ${active.time}` : ""}</h2>
              {(active?.holes || []).length === 0 ? <p className="text-sm text-[#94A3B8]">Esperando la rifa…</p> : (
                <div className="grid grid-cols-2 gap-2">
                  {active.holes.map((h) => (
                    <div key={h.hole} className="border border-gray-100 rounded-lg p-2">
                      <div className="text-xs font-bold text-[#1B4D3E]">Hoyo {h.hole}</div>
                      {h.teams.map((tm) => <div key={tm.team_id} className="text-[11px] truncate"><b className="text-[#1B4D3E]">{tm.slot}</b> {tm.name}</div>)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
